import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  Switch,
  Spinner,
} from '@nextui-org/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiKeyService, TimelineData } from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface TimelineChartProps {
  className?: string;
  dateRange?: DateRange;
  apiKeyId?: string;
}

type MetricType = 'requests' | 'tokens' | 'performance' | 'errors';

const TimelineChart: React.FC<TimelineChartProps> = ({
  className = '',
  dateRange,
  apiKeyId,
}) => {
  const [timelineData, setTimelineData] = useState<TimelineData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('requests');
  const [showStacked, setShowStacked] = useState(false);
  const [days] = useState(30);

  const loadTimelineData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use date range if provided, otherwise use days
      const response = dateRange?.isAllTime
        ? await apiKeyService.getUsageTimeline(
            30,
            undefined,
            undefined,
            true,
            apiKeyId
          ) // All time with proper flag
        : dateRange?.startDate && dateRange?.endDate && !dateRange.isAllTime
          ? await apiKeyService.getUsageTimeline(
              days,
              dateRange.startDate,
              dateRange.endDate,
              false,
              apiKeyId
            )
          : await apiKeyService.getUsageTimeline(
              days,
              undefined,
              undefined,
              false,
              apiKeyId
            );
      // Ensure all numeric values are properly processed
      const processedData = (response.timeline || []).map((item) => ({
        ...item,
        requests: Number(item.requests) || 0,
        input_tokens: Number(item.input_tokens) || 0,
        output_tokens: Number(item.output_tokens) || 0,
        avg_response_time: Number(item.avg_response_time) || 0,
        error_rate: Number(item.error_rate) || 0,
      }));
      setTimelineData(processedData);
    } catch (err) {
      console.error('Failed to load timeline data:', err);
      setError('Failed to load timeline data');
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  }, [days, dateRange, apiKeyId]);

  useEffect(() => {
    loadTimelineData();
  }, [loadTimelineData]);

  // Format data for different metrics
  const getChartData = () => {
    return timelineData.map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      requests: Number(item.requests) || 0,
      input_tokens: Number(item.input_tokens) || 0,
      output_tokens: Number(item.output_tokens) || 0,
      avg_response_time: Number(item.avg_response_time) || 0,
      error_rate: Number(item.error_rate) || 0,
      total_tokens:
        (Number(item.input_tokens) || 0) + (Number(item.output_tokens) || 0),
    }));
  };

  const getMetricConfig = (metric: MetricType) => {
    const configs = {
      requests: {
        title: 'API Requests',
        dataKey: 'requests',
        color: '#8884d8',
        yAxisLabel: 'Requests',
        formatter: (value: number) => value.toLocaleString(),
        type: 'single' as const,
      },
      tokens: {
        title: 'Token Usage',
        dataKeys: ['input_tokens', 'output_tokens'],
        colors: ['#8884d8', '#82ca9d'],
        yAxisLabel: 'Tokens',
        formatter: (value: number) => value.toLocaleString(),
        stacked: true,
        type: 'multiple' as const,
      },
      performance: {
        title: 'Response Time',
        dataKey: 'avg_response_time',
        color: '#ffc658',
        yAxisLabel: 'Time (ms)',
        formatter: (value: number) => `${value.toFixed(2)}ms`,
        type: 'single' as const,
      },
      errors: {
        title: 'Error Rate',
        dataKey: 'error_rate',
        color: '#ff7300',
        yAxisLabel: 'Error Rate (%)',
        formatter: (value: number) => `${value.toFixed(2)}%`,
        type: 'single' as const,
      },
    };
    return configs[metric];
  };

  const renderChart = () => {
    const data = getChartData();
    const config = getMetricConfig(selectedMetric);

    if (!data.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-sm font-medium mb-2">
              No timeline data available
            </p>
            <p className="text-xs text-gray-400">
              Data will appear once you start making API calls
            </p>
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            label={{
              value: config.yAxisLabel,
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '12px',
            }}
            formatter={(value: any, name?: string | number) => [
              config.formatter(value),
              String(name)
                ?.replace('_', ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase()),
            ]}
          />

          {selectedMetric === 'tokens' ? (
            <>
              <Legend />
              {showStacked ? (
                <>
                  <Bar
                    dataKey="input_tokens"
                    stackId="1"
                    fill="#8884d8"
                    name="Input Tokens"
                  />
                  <Bar
                    dataKey="output_tokens"
                    stackId="1"
                    fill="#82ca9d"
                    name="Output Tokens"
                  />
                </>
              ) : (
                <>
                  <Bar
                    dataKey="input_tokens"
                    fill="#8884d8"
                    name="Input Tokens"
                  />
                  <Bar
                    dataKey="output_tokens"
                    fill="#82ca9d"
                    name="Output Tokens"
                  />
                </>
              )}
            </>
          ) : config.type === 'single' ? (
            <Bar dataKey={config.dataKey} fill={config.color} />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Usage Timeline</h3>
        </CardHeader>
        <CardBody className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center w-full">
          <h3 className="text-lg font-semibold">Usage Timeline</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                isSelected={showStacked}
                onValueChange={setShowStacked}
              />
              <span className="text-sm text-gray-600">Stacked Bars</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error ? (
          <div className="text-center text-red-500 py-8">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <Tabs
              selectedKey={selectedMetric}
              onSelectionChange={(key) => setSelectedMetric(key as MetricType)}
              className="mb-4"
            >
              <Tab key="requests" title="Requests" />
              <Tab key="tokens" title="Tokens" />
              <Tab key="performance" title="Performance" />
              <Tab key="errors" title="Errors" />
            </Tabs>
            {renderChart()}

            {timelineData.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Total Requests</p>
                  <p className="font-semibold">
                    {timelineData
                      .reduce((sum, item) => sum + item.requests, 0)
                      .toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Total Tokens</p>
                  <p className="font-semibold">
                    {timelineData
                      .reduce(
                        (sum, item) =>
                          sum + item.input_tokens + item.output_tokens,
                        0
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Avg Response Time</p>
                  <p className="font-semibold">
                    {(
                      timelineData.reduce(
                        (sum, item) => sum + item.avg_response_time,
                        0
                      ) / timelineData.length
                    ).toFixed(2)}
                    ms
                  </p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Avg Error Rate</p>
                  <p className="font-semibold">
                    {(
                      timelineData.reduce(
                        (sum, item) => sum + item.error_rate,
                        0
                      ) / timelineData.length
                    ).toFixed(2)}
                    %
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default TimelineChart;
